/**
 * Settings Page - Clean Design
 */

import { useState } from 'react';
import { useAppStore } from '../stores';

type TabType = 'general' | 'datasources' | 'storage' | 'apikeys';

export function Settings() {
    const { currentApp } = useAppStore();
    const [activeTab, setActiveTab] = useState<TabType>('general');
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState('');

    // Form state
    const [settings, setSettings] = useState({
        name: currentApp?.name || 'My App',
        description: currentApp?.description || '',
        locale: 'en',
        publicApi: true,
        edgeCaching: true,
        cacheDuration: 3600,
    });

    const handleSave = async () => {
        setSaving(true);
        try {
            // Mock API call
            await new Promise(resolve => setTimeout(resolve, 800));
            setMessage('✅ Settings updated successfully');
            setTimeout(() => setMessage(''), 3000);
        } catch (e) {
            setMessage('❌ Failed to update settings');
        } finally {
            setSaving(false);
        }
    };

    const tabs: { id: TabType; label: string; icon: string }[] = [
        { id: 'general', label: 'General', icon: '⚙️' },
        { id: 'datasources', label: 'Data Sources', icon: '🗄️' },
        { id: 'storage', label: 'Storage', icon: '📦' },
        { id: 'apikeys', label: 'API Access', icon: '🔑' },
    ];

    return (
        <div className="animate-fade-in max-w-5xl mx-auto">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                <div>
                    <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
                        Settings
                    </h1>
                    <p style={{ color: 'var(--text-secondary)' }}>
                        Configure <span className="font-medium" style={{ color: 'var(--accent)' }}>{currentApp?.name || 'Project'}</span> environment
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    {message && (
                        <span className="text-sm font-medium" style={{ color: message.includes('✅') ? 'var(--success)' : 'var(--danger)' }}>
                            {message}
                        </span>
                    )}
                    <button
                        className="btn btn-primary px-8"
                        onClick={handleSave}
                        disabled={saving}
                    >
                        {saving ? 'Saving...' : 'Save Settings'}
                    </button>
                </div>
            </div>

            <div className="flex flex-col md:flex-row gap-8">
                {/* Tabs Sidebar */}
                <aside className="w-full md:w-64 space-y-1">
                    {tabs.map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${activeTab === tab.id
                                    ? 'bg-blue-500/10 font-bold'
                                    : 'hover:bg-opacity-5'
                                }`}
                            style={{
                                color: activeTab === tab.id ? 'var(--accent)' : 'var(--text-secondary)',
                                backgroundColor: activeTab === tab.id ? 'rgba(59, 130, 246, 0.1)' : 'transparent'
                            }}
                        >
                            <span>{tab.icon}</span>
                            <span>{tab.label}</span>
                        </button>
                    ))}
                </aside>

                {/* Main Content */}
                <div className="flex-1 space-y-6">
                    {activeTab === 'general' && (
                        <div className="space-y-6">
                            <div className="card p-6">
                                <h2 className="text-sm font-bold uppercase tracking-widest mb-6" style={{ color: 'var(--text-muted)' }}>
                                    Basic Information
                                </h2>
                                <div className="space-y-4">
                                    <div>
                                        <label className="label">Project Name</label>
                                        <input
                                            type="text"
                                            className="input"
                                            value={settings.name}
                                            onChange={(e) => setSettings({ ...settings, name: e.target.value })}
                                        />
                                    </div>
                                    <div>
                                        <label className="label">Description</label>
                                        <textarea
                                            className="input resize-none"
                                            rows={3}
                                            placeholder="What is this project about?"
                                            value={settings.description}
                                            onChange={(e) => setSettings({ ...settings, description: e.target.value })}
                                        />
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="label">Language</label>
                                            <select
                                                className="input"
                                                value={settings.locale}
                                                onChange={(e) => setSettings({ ...settings, locale: e.target.value })}
                                            >
                                                <option value="en">English (US)</option>
                                                <option value="hi">Hindi (IN)</option>
                                                <option value="es">Spanish</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="label">Timezone</label>
                                            <select className="input cursor-not-allowed opacity-50" disabled>
                                                <option>UTC +5:30 (IST)</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="card p-6">
                                <h2 className="text-sm font-bold uppercase tracking-widest mb-6" style={{ color: 'var(--text-muted)' }}>
                                    Performance & Caching
                                </h2>
                                <div className="space-y-6">
                                    <div className="flex items-center justify-between p-4 rounded-xl" style={{ backgroundColor: 'var(--bg-secondary)' }}>
                                        <div>
                                            <div className="font-bold" style={{ color: 'var(--text-primary)' }}>Edge Caching</div>
                                            <div className="text-xs" style={{ color: 'var(--text-muted)' }}>Serve content via Cloudflare Global Network</div>
                                        </div>
                                        <input
                                            type="checkbox"
                                            className="w-5 h-5"
                                            checked={settings.edgeCaching}
                                            onChange={(e) => setSettings({ ...settings, edgeCaching: e.target.checked })}
                                        />
                                    </div>

                                    <div>
                                        <label className="label">Cache Time-to-Live (Seconds)</label>
                                        <input
                                            type="number"
                                            className="input max-w-[200px]"
                                            value={settings.cacheDuration}
                                            onChange={(e) => setSettings({ ...settings, cacheDuration: parseInt(e.target.value) })}
                                        />
                                        <p className="text-[10px] mt-2" style={{ color: 'var(--text-muted)' }}>
                                            Higher values improve performance but updates may take longer to propagate.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="card p-6 border-red-500/20" style={{ borderColor: 'rgba(239, 68, 68, 0.2)' }}>
                                <h2 className="text-sm font-bold uppercase tracking-widest mb-6" style={{ color: 'var(--danger)' }}>
                                    Danger Zone
                                </h2>
                                <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
                                    Permanently delete this project and all its content. This action cannot be undone.
                                </p>
                                <button className="btn btn-danger px-6 font-bold shadow-lg shadow-red-500/20">
                                    Delete This Project
                                </button>
                            </div>
                        </div>
                    )}

                    {activeTab === 'datasources' && (
                        <div className="card p-6">
                            <h2 className="text-sm font-bold uppercase tracking-widest mb-6" style={{ color: 'var(--text-muted)' }}>
                                Connected Databases
                            </h2>
                            <div className="space-y-4">
                                <div className="p-5 rounded-xl border flex items-center justify-between" style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}>
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 rounded-xl bg-orange-500/10 flex items-center justify-center text-2xl">
                                            🧡
                                        </div>
                                        <div>
                                            <div className="font-bold" style={{ color: 'var(--text-primary)' }}>Cloudflare D1</div>
                                            <div className="text-xs" style={{ color: 'var(--text-muted)' }}>SQLite at the Edge</div>
                                        </div>
                                    </div>
                                    <span className="px-3 py-1 bg-green-500/10 text-green-400 text-xs font-bold rounded-full">ACTIVE</span>
                                </div>

                                <button className="btn btn-secondary w-full py-4 border-dashed bg-transparent border-2">
                                    + Connect External Database (Postgres/Supabase)
                                </button>
                            </div>
                        </div>
                    )}

                    {activeTab === 'storage' && (
                        <div className="card p-6">
                            <h2 className="text-sm font-bold uppercase tracking-widest mb-6" style={{ color: 'var(--text-muted)' }}>
                                Media Storage
                            </h2>
                            <div className="space-y-4">
                                <div className="p-5 rounded-xl border flex items-center justify-between" style={{ backgroundColor: 'var(--bg-secondary)', borderColor: 'var(--border-color)' }}>
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center text-2xl">
                                            ☁️
                                        </div>
                                        <div>
                                            <div className="font-bold" style={{ color: 'var(--text-primary)' }}>Cloudflare R2</div>
                                            <div className="text-xs" style={{ color: 'var(--text-muted)' }}>S3-Compatible Object Storage</div>
                                        </div>
                                    </div>
                                    <span className="px-3 py-1 bg-green-500/10 text-green-400 text-xs font-bold rounded-full">CONNECTED</span>
                                </div>

                                <button className="btn btn-secondary w-full py-4 border-dashed bg-transparent border-2">
                                    + Add New Provider (AWS S3)
                                </button>
                            </div>
                        </div>
                    )}

                    {activeTab === 'apikeys' && (
                        <div className="card p-6">
                            <h2 className="text-sm font-bold uppercase tracking-widest mb-6" style={{ color: 'var(--text-muted)' }}>
                                Developer Access
                            </h2>
                            <div className="space-y-6">
                                <div>
                                    <label className="label">Public Content Endpoint</label>
                                    <div className="flex gap-2">
                                        <input
                                            readOnly
                                            className="input bg-secondary font-mono text-xs flex-1"
                                            style={{ backgroundColor: 'var(--bg-secondary)' }}
                                            value={`https://cms.tenthprime.in/api/v1/content/${currentApp?.id}/[slug]`}
                                        />
                                        <button className="btn btn-secondary text-xs">Copy</button>
                                    </div>
                                </div>

                                <hr style={{ borderColor: 'var(--border-color)' }} />

                                <div className="flex items-center justify-between">
                                    <div>
                                        <h3 className="font-bold" style={{ color: 'var(--text-primary)' }}>API Tokens</h3>
                                        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Manage keys for write access</p>
                                    </div>
                                    <button className="btn btn-primary text-xs px-4">+ Generate Token</button>
                                </div>

                                <div className="text-center py-8 rounded-xl" style={{ backgroundColor: 'var(--bg-secondary)' }}>
                                    <p className="text-sm italic" style={{ color: 'var(--text-muted)' }}>No tokens generated yet.</p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
